import { useState, useRef, useCallback, useEffect } from 'react';

/** Material UI */
import { Box, Typography, LinearProgress, IconButton } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';

/** Auth0 */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** API & socket */
import { parseDocument, DOCUMENT_STATUS } from 'shared/api/documentUploadApi';
import { getSocket } from 'socket';

const ACCEPT = '.pdf';
const MAX_FILES = 20;

/**
 * Bottom of the New Shipment dialog only: drop PDFs → show parsing progress → apply to the form as soon as parsing finishes.
 * No DocumentUpload dialog, preview, or Apply button here.
 */
export default function NewShipmentDocumentDropZone({ tabValue, onApplyParsedData }) {
    const { getAccessTokenSilently } = useAuth0();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const socket = getSocket();
    const sid = socket?.id ?? '';

    /** Files: { id, file, name, status, progress, parsedData, error, document_type } */
    const [files, setFiles] = useState([]);
    const [dragging, setDragging] = useState(false);

    const processedIdsRef = useRef(new Set());

    const filesRef = useRef(files);
    filesRef.current = files;

    const isMountedRef = useRef(true);
    const abortControllersRef = useRef(new Set());

    useEffect(() => {
        isMountedRef.current = true;
        const controllers = abortControllersRef.current;
        return () => {
            isMountedRef.current = false;
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, []);

    const addFiles = useCallback(
        (fileList) => {
            const arr = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
            if (arr.length === 0) return;

            const remain = MAX_FILES - filesRef.current.length;
            if (remain <= 0) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: `Up to ${MAX_FILES} files can be uploaded at a time.`,
                        severity: 'warning',
                    })
                );
                return;
            }
            const accepted = arr.slice(0, remain);
            if (accepted.length < arr.length) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: `Only ${accepted.length} of ${arr.length} files were added (max ${MAX_FILES}).`,
                        severity: 'warning',
                    })
                );
            }

            setFiles((prev) => [
                ...prev,
                ...accepted.map((file) => ({
                    id: randomId(),
                    file,
                    name: file.name,
                    status: DOCUMENT_STATUS.PENDING,
                    progress: 0,
                    parsedData: null,
                    error: null,
                    document_type: null,
                })),
            ]);
        },
        [dispatch]
    );

    const removeFile = useCallback((id) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
        processedIdsRef.current.delete(id);
    }, []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer?.files);
        },
        [addFiles]
    );

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
    }, []);

    const handleInputChange = useCallback(
        (e) => {
            addFiles(e.target?.files);
            e.target.value = '';
        },
        [addFiles]
    );

    /** Bundle every successfully parsed file into one mblData/hblList payload and apply it in a single pass.
     *
     *  Applying file by file would produce the same values (the apply side reads the latest state right
     *  after each dispatch), but bundling fills the main (MBL) fields and the house tabs together and
     *  shows a single notification, so it reads as "one set of documents was applied". */
    const applyBatch = useCallback(
        (items) => {
            if (!onApplyParsedData) return;
            const valid = items.filter((f) => f.parsedData && typeof f.parsedData === 'object');
            if (valid.length === 0) return;

            const isMblOrMawb = (dt) => ['MBL', 'MAWB'].includes((dt || '').toUpperCase());
            const isHblOrHawb = (dt) => ['HBL', 'HAWB'].includes((dt || '').toUpperCase());

            const houseItems = valid.filter((f) => isHblOrHawb(f.document_type));
            const mainItem =
                valid.find((f) => isMblOrMawb(f.document_type)) ??
                valid.find((f) => !isHblOrHawb(f.document_type)) ??
                null;

            onApplyParsedData({
                mblData: mainItem ? { parsedData: mainItem.parsedData } : null,
                hblList: houseItems.map((f) => ({ parsedData: f.parsedData })),
            });
        },
        [onApplyParsedData]
    );

    const runParse = useCallback(
        async (item, signal) => {
            setFiles((prev) =>
                prev.map((f) => (f.id === item.id ? { ...f, status: DOCUMENT_STATUS.PARSING, progress: 0 } : f))
            );
            try {
                const result = await parseDocument({
                    file: item.file,
                    shipmentType: tabValue,
                    autoCreate: false,
                    sid,
                    getAccessTokenSilently,
                    signal,
                    onUploadProgress: (pct) => {
                        setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, progress: pct } : f)));
                    },
                });
                if (result.status === 200 && result.parsed_data) {
                    const updated = {
                        ...item,
                        status: DOCUMENT_STATUS.SUCCESS,
                        progress: 100,
                        parsedData: result.parsed_data,
                        document_type: result.document_type ?? 'UNKNOWN',
                        error: null,
                    };
                    setFiles((prev) =>
                        prev.map((f) => (f.id === item.id ? updated : f))
                    );
                    
                    return updated;
                }
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? { ...f, status: DOCUMENT_STATUS.FAILED, error: result.message || 'Parsing failed' }
                            : f
                    )
                );
                return null;
            } catch (err) {
                if (signal?.aborted) return null;
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? {
                                  ...f,
                                  status: DOCUMENT_STATUS.FAILED,
                                  error: err.response?.data?.message || err.message || 'Upload failed',
                              }
                            : f
                    )
                );
                return null;
            }
        },
        [tabValue, sid, getAccessTokenSilently]
    );

    const processBatch = useCallback(async () => {
        const pending = filesRef.current.filter(
            (f) => f.status === DOCUMENT_STATUS.PENDING && !processedIdsRef.current.has(f.id)
        );
        if (pending.length === 0) return;

        pending.forEach((f) => processedIdsRef.current.add(f.id));

        const controller = new AbortController();
        abortControllersRef.current.add(controller);
        let parsed;
        try {
            parsed = (await Promise.all(pending.map((item) => runParse(item, controller.signal)))).filter(Boolean);
        } finally {
            abortControllersRef.current.delete(controller);
        }

        if (!isMountedRef.current) return;

        const aliveIds = new Set(filesRef.current.map((f) => f.id));
        const applicable = parsed.filter((f) => aliveIds.has(f.id));
        if (applicable.length === 0) return;

        try {
            applyBatch(applicable);
        } catch (err) {
            const failedIds = new Set(applicable.map((f) => f.id));
            setFiles((prev) =>
                prev.map((f) =>
                    failedIds.has(f.id)
                        ? { ...f, status: DOCUMENT_STATUS.FAILED, error: err.message || 'Failed to apply parsed data' }
                        : f
                )
            );
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Parsed document(s) could not be applied to the form.',
                    severity: 'error',
                })
            );
            return;
        }
        const appliedIds = new Set(applicable.map((f) => f.id));
        appliedIds.forEach((id) => processedIdsRef.current.delete(id));
        setFiles((prev) => prev.filter((f) => !appliedIds.has(f.id)));
    }, [runParse, applyBatch, dispatch]);

    const pendingKey = files
        .filter((f) => f.status === DOCUMENT_STATUS.PENDING)
        .map((f) => f.id)
        .join('|');

    useEffect(() => {
        if (pendingKey) processBatch();
    }, [pendingKey, processBatch]);

    return (
        <Box sx={{ px: 3.5, pb: 2, flexShrink: 0 }}>
            <Box
                sx={{
                    py: 1.5,
                    px: 2,
                    border: '2px dashed',
                    borderColor: dragging ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: dragging ? 'action.hover' : 'grey.50',
                    textAlign: 'center',
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <UploadFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <b>Drop PDFs here</b> or{' '}
                    <Typography
                        component="span"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        browse
                    </Typography>
                    {' — parsed data is applied to the form automatically.'}
                </Typography>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    hidden
                    onChange={handleInputChange}
                />
            </Box>

            {files.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {files.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                mb: 0.5,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Typography noWrap variant="body2" sx={{ flex: 1, fontSize: '12px' }}>
                                    {item.name}
                                </Typography>
                                {item.status === DOCUMENT_STATUS.PENDING && (
                                    <Typography variant="caption" color="text.secondary">
                                        Pending
                                    </Typography>
                                )}
                                {item.status === DOCUMENT_STATUS.PARSING && (
                                    <Box sx={{ width: 72 }}>
                                        <LinearProgress variant="determinate" value={item.progress} sx={{ height: 6, borderRadius: 1 }} />
                                    </Box>
                                )}
                                {item.status === DOCUMENT_STATUS.SUCCESS && (
                                    <CheckCircleIcon color="primary" fontSize="small" />
                                )}
                                {item.status === DOCUMENT_STATUS.FAILED && (
                                    <ErrorIcon color="error" fontSize="small" titleAccess={item.error} />
                                )}
                            </Box>
                            <IconButton size="small" onClick={() => removeFile(item.id)} aria-label="Remove">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
